<template>
  <div
    ref="dropdown"
    :class="componentClasses"
  >
    <slot
      name="trigger"
      :toggle="toggleDropdown"
    >
      <div class="stk-dropdown-trigger">
        <Button
          :icon-before="iconBefore"
          :fullwidth="fullwidth"
          :disabled="disabled"
          :icon-after="disabled ? '' : 'fas fa-chevron-down'"
          class="stk-dropdown-button"
          @click="toggleDropdown"
        >
          {{ label }}
        </Button>
      </div>
    </slot>

    <div
      class="stk-dropdown-box"
      :style="computedStyle"
    >
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import Button from '../button/Button.vue';

const props = defineProps({
  anchor: {
    type: String,
    default: 'left',
  },
  label: {
    type: String,
    required: false,
    default: null,
  },
  iconBefore: {
    type: String,
    required: false,
    default: null,
  },
  fullwidth: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  static: {
    type: Boolean,
    required: false,
    default: false,
  },
  width: {
    type: String,
    required: false,
    default: null,
  },
});

const isOpen = ref(false);
const dropdown = ref(null);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = event => {
  isOpen.value = false;
};

const handleClickOutside = event => {
  if (dropdown.value && !dropdown.value.contains(event.target)) {
    closeDropdown();
  }
};

const handleEscape = event => {
  if (event.key === 'Escape') {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('keydown', handleEscape);
});

const computedStyle = computed(() => {
  return props.width ? { width: props.width } : undefined;
});

const componentClasses = computed(() => [
  'stk-dropdown',
  isOpen.value ? `is-open` : '',
  props.fullwidth ? `is-fullwidth` : '',
  props.static ? `is-static` : '',
  props.anchor ? `is-anchored-${props.anchor}` : 'is-anchored-left',
]);
</script>

<style>
.stk-dropdown {
  display: inline-flex;
  vertical-align: top;
  position: relative;
  justify-content: start;

  &.is-fullwidth {
    &,
    .stk-dropdown-box,
    .stk-dropdown-trigger {
      display: flex;
      width: 100%;
    }
  }

  &.is-static {
    flex-direction: column;
    gap: 0.5rem;

    .stk-dropdown-box {
      position: static;
      width: 100%;
    }
  }
}

.stk-dropdown-trigger {
  display: inline-flex;
  vertical-align: top;
}

.stk-dropdown-box {
  background-color: var(--stk-control-background-color);
  border-color: var(--stk-control-border-color);
  border-radius: 1em;
  border-style: solid;
  border-width: var(--stk-control-border-width);
  box-shadow: var(--stk-control-shadow);
  display: flex;
  flex-direction: column;
  position: absolute;
  top: calc(100% + 0.5rem);
  opacity: 0;
  padding: 0.5rem;
  pointer-events: none;
  transform: translateY(-0.25rem);
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity, transform;
  width: 16rem;
  z-index: 10;
}

.stk-dropdown-button {
  justify-content: space-between;
}

.stk-dropdown {
  &.is-anchored-left {
    .stk-dropdown-box {
      left: 0;
      right: auto;
    }
  }

  &.is-anchored-right {
    .stk-dropdown-box {
      left: auto;
      right: 0;
    }
  }

  &.is-open {
    .stk-dropdown-box {
      opacity: 1;
      pointer-events: auto;
      transform: none;
    }
  }
}
</style>
